import { Column, ColumnConfig } from '@ant-design/charts';
import { useLazyQuery } from '@apollo/client';
import { GET_IDEA_REGION } from '@frontend/data-access';
import { Card, Select } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

const filterOptions = [
  { label: 'Drafted', value: 'DRAFT' },
  { label: 'Published', value: 'PUBLISHED' },
  { label: 'Hidden', value: 'HIDDEN' },
  { label: 'Reported', value: 'REPORTED' },
];

function PopularRegionStats() {
  const { t } = useTranslation('admin');

  const [filter, setFilter] = useState<string[]>([
    'DRAFT',
    'PUBLISHED',
    'HIDDEN',
    'REPORTED',
  ]);
  const [getIdeaRegions, { data, loading }] = useLazyQuery<{
    dashboardTopIdeaRegions: { name: string; count: number }[];
  }>(GET_IDEA_REGION, {
    fetchPolicy: 'no-cache',
  });

  const highestRegion = useMemo(() => {
    if (
      !data?.dashboardTopIdeaRegions ||
      data.dashboardTopIdeaRegions.length === 0
    )
      return '';
    return data.dashboardTopIdeaRegions.reduce((max, item) =>
      item.count > max.count ? item : max
    ).name;
  }, [data]);

  const formattedData = useMemo(() => {
    return data?.dashboardTopIdeaRegions.map((item) => ({
      ...item,
      color: item.name === highestRegion ? 'green' : 'blue',
    }));
  }, [data, highestRegion]);

  const config: ColumnConfig = {
    data: formattedData || [],
    xField: 'name',
    yField: 'count',
    appendPadding: [20, 0, 0, 0],
    color: ({ name }) => (name === highestRegion ? '#63E063' : '#2888ED'),
    xAxis: {
      label: {
        formatter: (value) =>
          value.length > 30 ? value.slice(0, 30) + '...' : value,
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
      },
    },
    yAxis: {
      label: {
        style: {
          fontFamily: 'Lufga',
          fill: 'rgba(0, 0, 0, 0.65)',
          textAlign: 'right',
        },
      },
    },
    meta: {
      type: {
        alias: t('region'),
      },
      count: {
        alias: t('idea_count'),
      },
    },
    loading: loading,
    label: {
      text: 'count',
      position: 'top',
      textAlign: 'center',
      dy: -15,
      style: {
        fill: 'rgba(0, 0, 0, 0.65)',
        fontFamily: 'Lufga',
      },
    },
    interaction: {
      tooltip: {
        name: 'name',
        render: (event, { title, items }) => {
          return (
            <div>
              <div>{title}</div>
              <div>
                {t('count')}: {items[0].value}
              </div>
            </div>
          );
        },
      },
    },
  };

  useEffect(() => {
    getIdeaRegions({
      variables: {
        statuses: filter,
      },
    });
  }, [filter, getIdeaRegions]);

  const handleFilterChange = (value: string[]) => {
    setFilter(value);
  };

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
    >
      <div className="mb-5">
        <h2>{t('idea_by_region')}</h2>
        <Select
          onChange={handleFilterChange}
          options={filterOptions}
          value={filter}
          placeholder=""
          allowClear
          mode="multiple"
          style={{ width: '100%' }}
          maxTagCount="responsive"
          optionFilterProp="label"
        />
      </div>
      <Column {...config} />
    </Card>
  );
}

export default PopularRegionStats;
